import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { motion } from 'framer-motion';
import { Heart, Users, MessageCircleHeart, Sparkles, HandHeart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router-dom';
import { useDocumentHead } from '@/hooks/useDocumentHead';

const copy = {
  en: {
    badge: 'Our Purpose',
    title: 'Why Say It NOW Exists',
    subtitle: 'Because the living can hear us now.',
    story1: 'The purpose of creating this app is simple, yet deeply human: to create better connections between people while we still can.',
    story2: 'Too often, we don\'t truly hear how loved, appreciated, or important someone is until after they\'re gone. For years, I\'ve seen the same pattern repeat itself—someone passes away, and suddenly their social media fills with posts describing how amazing they were, how much they meant, how deeply they\'ll be missed. But when you look back through their story, their timeline, their life—those words were rarely said while they were here to receive them.',
    story3: 'People write messages to the deceased as if they can read them. Maybe they can. Maybe they can\'t. I\'ve always wondered. But what we do know is this: the living can hear us now.',
    story4: 'Say It NOW exists to remind us to say the words before it\'s too late. It gives people a space—and a reason—to express love, gratitude, admiration, and appreciation while the person is still alive, still present, still able to feel the impact of those words.',
    highlight: 'Sometimes, all a person needs is to know that someone cares. To know that they matter. To know that they are seen, valued, and loved. Those words can change someone\'s day—and sometimes, they can change someone\'s life. They matter deeply for mental and emotional health, even if we don\'t always realize it.',
    story5: 'Life gets busy. We get distracted. We assume there will be time later. And too often, later never comes.',
    closing1: 'Say It NOW was built so that no one leaves this world without knowing how loved, appreciated, or incredible they truly are.',
    closing2: 'Say it now.',
    closing3: 'While they\'re still here.',
    valuesTitle: 'Our Core Values',
    value1Title: 'Express Love Now', value1Desc: 'Don\'t wait for a funeral to share how much someone means to you.',
    value2Title: 'Human Connection', value2Desc: 'Building bridges between hearts through meaningful words.',
    value3Title: 'Mental Wellness', value3Desc: 'Knowing you\'re loved and appreciated improves mental health.',
    value4Title: 'Never Forgotten', value4Desc: 'Ensuring everyone knows their worth before it\'s too late.',
    ctaTitle: 'Ready to Express Your Love?',
    ctaSubtitle: 'Start creating beautiful Living Announcements today. It only takes a few minutes to brighten someone\'s day.',
    ctaButton: 'Get Started Free',
    ctaSecondary: 'View Pricing',
  },
  es: {
    badge: 'Nuestro Propósito',
    title: 'Por Qué Existe Say It NOW',
    subtitle: 'Porque los que viven pueden escucharnos ahora.',
    story1: 'El propósito de crear esta aplicación es simple, pero profundamente humano: crear mejores conexiones entre las personas mientras aún podemos.',
    story2: 'Demasiado a menudo, no escuchamos lo querido, apreciado o importante que es alguien hasta después de que se ha ido. Durante años, he visto el mismo patrón repetirse—alguien fallece, y de repente sus redes sociales se llenan de publicaciones describiendo lo increíble que era, cuánto significaba, cuánto lo extrañarán. Pero cuando miras hacia atrás en su historia, su línea de tiempo, su vida—esas palabras rara vez se dijeron mientras estaba aquí para recibirlas.',
    story3: 'Las personas escriben mensajes a los fallecidos como si pudieran leerlos. Tal vez pueden. Tal vez no. Siempre me lo he preguntado. Pero lo que sí sabemos es esto: los que viven pueden escucharnos ahora.',
    story4: 'Say It NOW existe para recordarnos decir las palabras antes de que sea demasiado tarde. Brinda a las personas un espacio—y una razón—para expresar amor, gratitud, admiración y aprecio mientras la persona aún está viva, presente y puede sentir el impacto de esas palabras.',
    highlight: 'A veces, todo lo que una persona necesita es saber que alguien se preocupa. Saber que importa. Saber que es vista, valorada y amada. Esas palabras pueden cambiar el día de alguien—y a veces, pueden cambiar su vida. Son profundamente importantes para la salud mental y emocional, aunque no siempre nos demos cuenta.',
    story5: 'La vida se pone ocupada. Nos distraemos. Asumimos que habrá tiempo después. Y muy a menudo, ese después nunca llega.',
    closing1: 'Say It NOW fue creado para que nadie deje este mundo sin saber lo amado, apreciado o increíble que verdaderamente es.',
    closing2: 'Dilo ahora.',
    closing3: 'Mientras aún están aquí.',
    valuesTitle: 'Nuestros Valores',
    value1Title: 'Expresa Amor Ahora', value1Desc: 'No esperes a un funeral para compartir cuánto significa alguien para ti.',
    value2Title: 'Conexión Humana', value2Desc: 'Construyendo puentes entre corazones a través de palabras significativas.',
    value3Title: 'Bienestar Mental', value3Desc: 'Saber que eres amado y apreciado mejora la salud mental.',
    value4Title: 'Nunca Olvidado', value4Desc: 'Asegurando que todos conozcan su valor antes de que sea tarde.',
    ctaTitle: '¿Listo para Expresar tu Amor?',
    ctaSubtitle: 'Comienza a crear hermosos Anuncios Vivientes hoy. Solo toma unos minutos alegrar el día de alguien.',
    ctaButton: 'Comenzar Gratis',
    ctaSecondary: 'Ver Precios',
  },
  pt: {
    badge: 'Nosso Propósito',
    title: 'Por Que o Say It NOW Existe',
    subtitle: 'Porque os vivos podem nos ouvir agora.',
    story1: 'O propósito de criar este aplicativo é simples, mas profundamente humano: criar melhores conexões entre as pessoas enquanto ainda podemos.',
    story2: 'Muitas vezes, não ouvimos o quão amado, apreciado ou importante alguém é até depois que se foi. Por anos, vi o mesmo padrão se repetir—alguém falece, e de repente suas redes sociais se enchem de postagens descrevendo o quão incrível era, quanto significava, quanto sentirão falta. Mas quando você olha para trás na história, na linha do tempo, na vida—essas palavras raramente foram ditas enquanto a pessoa estava aqui para recebê-las.',
    story3: 'As pessoas escrevem mensagens para os falecidos como se pudessem lê-las. Talvez possam. Talvez não. Sempre me perguntei. Mas o que sabemos é isto: os vivos podem nos ouvir agora.',
    story4: 'O Say It NOW existe para nos lembrar de dizer as palavras antes que seja tarde demais. Ele dá às pessoas um espaço—e uma razão—para expressar amor, gratidão, admiração e apreço enquanto a pessoa ainda está viva, presente e capaz de sentir o impacto dessas palavras.',
    highlight: 'Às vezes, tudo o que uma pessoa precisa é saber que alguém se importa. Saber que importa. Saber que é vista, valorizada e amada. Essas palavras podem mudar o dia de alguém—e às vezes, podem mudar sua vida. Elas são profundamente importantes para a saúde mental e emocional, mesmo que nem sempre percebamos.',
    story5: 'A vida fica corrida. Nos distraímos. Assumimos que haverá tempo depois. E muitas vezes, o depois nunca chega.',
    closing1: 'O Say It NOW foi criado para que ninguém deixe este mundo sem saber o quão amado, apreciado ou incrível verdadeiramente é.',
    closing2: 'Diga agora.',
    closing3: 'Enquanto ainda estão aqui.',
    valuesTitle: 'Nossos Valores',
    value1Title: 'Expresse Amor Agora', value1Desc: 'Não espere um funeral para compartilhar quanto alguém significa para você.',
    value2Title: 'Conexão Humana', value2Desc: 'Construindo pontes entre corações através de palavras significativas.',
    value3Title: 'Bem-estar Mental', value3Desc: 'Saber que é amado e apreciado melhora a saúde mental.',
    value4Title: 'Nunca Esquecido', value4Desc: 'Garantindo que todos conheçam seu valor antes que seja tarde.',
    ctaTitle: 'Pronto para Expressar Seu Amor?',
    ctaSubtitle: 'Comece a criar belos Anúncios Viventes hoje. Leva apenas alguns minutos para alegrar o dia de alguém.',
    ctaButton: 'Começar Gratis',
    ctaSecondary: 'Ver Preços',
  },
  fr: {
    badge: 'Notre Mission',
    title: 'Pourquoi Say It NOW Existe',
    subtitle: 'Parce que les vivants peuvent nous entendre maintenant.',
    story1: 'Le but de cette application est simple, mais profondément humain : créer de meilleures connexions entre les gens tant que nous le pouvons encore.',
    story2: 'Trop souvent, nous n\'entendons pas à quel point quelqu\'un est aimé, apprécié ou important jusqu\'à ce qu\'il soit parti. Pendant des années, j\'ai vu le même schéma se répéter—quelqu\'un décède, et soudain ses réseaux sociaux se remplissent de publications décrivant à quel point il était incroyable, combien il comptait, combien il va manquer. Mais quand on regarde en arrière dans son histoire, sa chronologie, sa vie—ces mots ont rarement été dits de son vivant.',
    story3: 'Les gens écrivent des messages aux défunts comme s\'ils pouvaient les lire. Peut-être qu\'ils le peuvent. Peut-être pas. Je me suis toujours posé la question. Mais ce que nous savons, c\'est ceci : les vivants peuvent nous entendre maintenant.',
    story4: 'Say It NOW existe pour nous rappeler de dire les mots avant qu\'il ne soit trop tard. Il donne aux gens un espace—et une raison—pour exprimer l\'amour, la gratitude, l\'admiration et l\'appréciation tant que la personne est encore en vie, présente et capable de ressentir l\'impact de ces mots.',
    highlight: 'Parfois, tout ce dont une personne a besoin, c\'est de savoir que quelqu\'un se soucie d\'elle. De savoir qu\'elle compte. De savoir qu\'elle est vue, valorisée et aimée. Ces mots peuvent changer la journée de quelqu\'un—et parfois, ils peuvent changer sa vie. Ils comptent profondément pour la santé mentale et émotionnelle, même si nous ne le réalisons pas toujours.',
    story5: 'La vie devient chargée. On se laisse distraire. On suppose qu\'il y aura du temps plus tard. Et trop souvent, plus tard ne vient jamais.',
    closing1: 'Say It NOW a été créé pour que personne ne quitte ce monde sans savoir à quel point il est aimé, apprécié ou incroyable.',
    closing2: 'Dites-le maintenant.',
    closing3: 'Tant qu\'ils sont encore là.',
    valuesTitle: 'Nos Valeurs',
    value1Title: 'Exprimez l\'Amour Maintenant', value1Desc: 'N\'attendez pas des funérailles pour partager combien quelqu\'un compte pour vous.',
    value2Title: 'Connexion Humaine', value2Desc: 'Construire des ponts entre les cœurs par des mots significatifs.',
    value3Title: 'Bien-être Mental', value3Desc: 'Savoir qu\'on est aimé et apprécié améliore la santé mentale.',
    value4Title: 'Jamais Oublié', value4Desc: 'S\'assurer que chacun connaît sa valeur avant qu\'il ne soit trop tard.',
    ctaTitle: 'Prêt à Exprimer Votre Amour ?',
    ctaSubtitle: 'Commencez à créer de belles Annonces Vivantes aujourd\'hui. Il ne faut que quelques minutes pour illuminer la journée de quelqu\'un.',
    ctaButton: 'Commencer Gratuitement',
    ctaSecondary: 'Voir les Tarifs',
  },
  ar: {
    badge: 'هدفنا',
    title: 'لماذا يوجد Say It NOW',
    subtitle: 'لأن الأحياء يمكنهم سماعنا الآن.',
    story1: 'الهدف من إنشاء هذا التطبيق بسيط، لكنه إنساني بعمق: خلق روابط أفضل بين الناس بينما لا يزال بإمكاننا ذلك.',
    story2: 'في كثير من الأحيان، لا نسمع عن مدى حب شخص ما أو تقديره أو أهميته إلا بعد رحيله. لسنوات، رأيت نفس النمط يتكرر—يرحل شخص ما، وفجأة تمتلئ حساباته على وسائل التواصل الاجتماعي بمنشورات تصف كم كان رائعاً، وكم كان يعني، وكم سيُفتقد. لكن عندما تنظر إلى الوراء في قصته، في سيرته—نادراً ما قيلت تلك الكلمات وهو هنا ليسمعها.',
    story3: 'يكتب الناس رسائل للمتوفين كما لو أنهم يستطيعون قراءتها. ربما يستطيعون. ربما لا. لطالما تساءلت. لكن ما نعرفه هو هذا: الأحياء يمكنهم سماعنا الآن.',
    story4: 'Say It NOW موجود ليذكرنا بقول الكلمات قبل فوات الأوان. يمنح الناس مساحة—وسبباً—للتعبير عن الحب والامتنان والإعجاب والتقدير بينما الشخص لا يزال حياً، حاضراً، وقادراً على الشعور بتأثير تلك الكلمات.',
    highlight: 'أحياناً، كل ما يحتاجه الشخص هو أن يعرف أن هناك من يهتم. أن يعرف أنه مهم. أن يعرف أنه مرئي ومقدّر ومحبوب. تلك الكلمات يمكن أن تغير يوم شخص—وأحياناً، يمكن أن تغير حياته. إنها مهمة جداً للصحة النفسية والعاطفية، حتى لو لم ندرك ذلك دائماً.',
    story5: 'الحياة تصبح مشغولة. نتشتت. نفترض أنه سيكون هناك وقت لاحقاً. وفي كثير من الأحيان، لا يأتي ذلك الوقت أبداً.',
    closing1: 'تم بناء Say It NOW حتى لا يغادر أحد هذا العالم دون أن يعرف كم هو محبوب ومقدّر ومذهل حقاً.',
    closing2: 'قلها الآن.',
    closing3: 'بينما لا يزالون هنا.',
    valuesTitle: 'قيمنا الأساسية',
    value1Title: 'عبّر عن الحب الآن', value1Desc: 'لا تنتظر جنازة لتشارك كم يعني شخص ما لك.',
    value2Title: 'التواصل الإنساني', value2Desc: 'بناء جسور بين القلوب من خلال كلمات ذات معنى.',
    value3Title: 'الصحة النفسية', value3Desc: 'معرفة أنك محبوب ومقدّر تحسّن الصحة النفسية.',
    value4Title: 'لن يُنسى أبداً', value4Desc: 'ضمان أن يعرف الجميع قيمتهم قبل فوات الأوان.',
    ctaTitle: 'مستعد للتعبير عن حبك؟',
    ctaSubtitle: 'ابدأ بإنشاء إعلانات حية جميلة اليوم. لا يتطلب الأمر سوى دقائق لإسعاد يوم شخص ما.',
    ctaButton: 'ابدأ مجاناً',
    ctaSecondary: 'عرض الأسعار',
  },
  ja: {
    badge: '私たちの目的',
    title: 'Say It NOWが存在する理由',
    subtitle: '生きている人は、今、私たちの声を聞くことができるから。',
    story1: 'このアプリを作った目的はシンプルでありながら、深く人間的なものです：まだできるうちに、人と人とのつながりをより良くすること。',
    story2: '私たちはあまりにも多く、誰かがどれほど愛され、感謝され、大切であるかを、その人がいなくなってから初めて聞きます。何年もの間、同じパターンの繰り返しを見てきました—誰かが亡くなり、突然そのSNSがその人がどれほど素晴らしかったか、どれほど意味があったかの投稿で溢れます。しかし、その人の人生を振り返ると—それらの言葉は、受け取れる間にはめったに言われませんでした。',
    story3: '人々は亡くなった人にメッセージを書きます、まるで読めるかのように。たぶん読めるのかもしれません。たぶん読めないのかもしれません。ずっと不思議に思っていました。しかし、確かなことがあります：生きている人は、今、私たちの声を聞くことができます。',
    story4: 'Say It NOWは、手遅れになる前に言葉を伝えることを思い出させるために存在します。人がまだ生きている間、まだそこにいる間、その言葉の影響を感じられる間に、愛、感謝、賞賛、感謝の気持ちを表現する場所と理由を提供します。',
    highlight: '時に、人に必要なのは、誰かが気にかけてくれていると知ること。自分が大切だと知ること。見られ、価値があり、愛されていると知ること。その言葉は誰かの一日を変えることができ—時には人生を変えることもあります。精神的・感情的健康にとって深く重要です。',
    story5: '人生は忙しくなります。私たちは気を取られます。後で時間があると思います。そして、あまりにも多く、その「後で」は来ません。',
    closing1: 'Say It NOWは、誰もが自分がどれほど愛され、感謝され、素晴らしいかを知らずにこの世を去ることがないように作られました。',
    closing2: '今、伝えよう。',
    closing3: 'まだここにいるうちに。',
    valuesTitle: '私たちの価値観',
    value1Title: '今、愛を伝える', value1Desc: '葬儀まで待たないで、誰かがどれほど大切かを共有しましょう。',
    value2Title: '人間のつながり', value2Desc: '意味のある言葉を通じて心と心に橋を架ける。',
    value3Title: '心の健康', value3Desc: '愛され感謝されていると知ることは、メンタルヘルスを改善します。',
    value4Title: '決して忘れない', value4Desc: '手遅れになる前に、すべての人が自分の価値を知ることを確実にする。',
    ctaTitle: '愛を伝える準備はできましたか？',
    ctaSubtitle: '今日から美しいリビングアナウンスメントを作り始めましょう。誰かの日を明るくするのにほんの数分で十分です。',
    ctaButton: '無料で始める',
    ctaSecondary: '料金を見る',
  },
  ko: {
    badge: '우리의 목적',
    title: 'Say It NOW가 존재하는 이유',
    subtitle: '살아있는 사람은 지금 우리의 말을 들을 수 있으니까요.',
    story1: '이 앱을 만든 목적은 단순하지만 깊이 인간적입니다: 아직 가능한 동안 사람들 사이에 더 나은 연결을 만드는 것.',
    story2: '너무나 자주, 우리는 누군가가 얼마나 사랑받고, 감사받고, 중요한지 그 사람이 떠난 후에야 듣습니다. 수년간 같은 패턴이 반복되는 것을 봤습니다—누군가가 세상을 떠나면, 갑자기 그들의 SNS가 그들이 얼마나 놀라웠는지, 얼마나 의미 있었는지의 글로 가득 찹니다. 하지만 그들의 이야기를 돌아보면—그 말들은 들을 수 있을 때 거의 하지 않았습니다.',
    story3: '사람들은 고인에게 읽을 수 있는 것처럼 메시지를 씁니다. 어쩌면 읽을 수 있을지도 모릅니다. 어쩌면 아닐지도. 항상 궁금했습니다. 하지만 확실한 것이 있습니다: 살아있는 사람은 지금 우리의 말을 들을 수 있습니다.',
    story4: 'Say It NOW는 너무 늦기 전에 말을 전하라고 상기시켜주기 위해 존재합니다. 사람이 아직 살아있고, 존재하고, 그 말의 영향을 느낄 수 있는 동안 사랑, 감사, 존경, 감사의 마음을 표현할 공간과 이유를 제공합니다.',
    highlight: '때로는, 사람에게 필요한 것은 누군가가 관심을 가지고 있다는 것을 아는 것입니다. 자신이 중요하다는 것을 아는 것. 보이고, 가치 있고, 사랑받고 있다는 것을 아는 것. 그 말은 누군가의 하루를 바꿀 수 있고—때로는 인생을 바꿀 수도 있습니다. 정신적, 감정적 건강에 깊이 중요합니다.',
    story5: '삶은 바빠집니다. 산만해집니다. 나중에 시간이 있을 거라 생각합니다. 그리고 너무 자주, 그 나중은 오지 않습니다.',
    closing1: 'Say It NOW는 누구도 자신이 얼마나 사랑받고, 감사받고, 놀라운 존재인지 모른 채 이 세상을 떠나지 않도록 만들어졌습니다.',
    closing2: '지금 말하세요.',
    closing3: '아직 곁에 있을 때.',
    valuesTitle: '핵심 가치',
    value1Title: '지금 사랑을 표현하세요', value1Desc: '장례식까지 기다리지 마세요. 누군가가 얼마나 소중한지 나누세요.',
    value2Title: '인간적 연결', value2Desc: '의미 있는 말을 통해 마음과 마음에 다리를 놓습니다.',
    value3Title: '정신 건강', value3Desc: '사랑받고 감사받고 있다는 것을 아는 것은 정신 건강을 개선합니다.',
    value4Title: '잊히지 않는', value4Desc: '너무 늦기 전에 모든 사람이 자신의 가치를 알 수 있도록 합니다.',
    ctaTitle: '사랑을 표현할 준비가 되셨나요?',
    ctaSubtitle: '오늘부터 아름다운 리빙 어나운스먼트를 만들어 보세요. 누군가의 하루를 밝히는 데 몇 분이면 충분합니다.',
    ctaButton: '무료로 시작하기',
    ctaSecondary: '가격 보기',
  },
  id: {
    badge: 'Tujuan Kami',
    title: 'Mengapa Say It NOW Ada',
    subtitle: 'Karena yang hidup dapat mendengar kita sekarang.',
    story1: 'Tujuan membuat aplikasi ini sederhana, namun sangat manusiawi: menciptakan koneksi yang lebih baik antar manusia selagi masih bisa.',
    story2: 'Terlalu sering, kita tidak benar-benar mendengar betapa dicintai, dihargai, atau pentingnya seseorang hingga setelah mereka pergi. Selama bertahun-tahun, saya melihat pola yang sama berulang—seseorang meninggal, dan tiba-tiba media sosial mereka dipenuhi postingan tentang betapa luar biasanya mereka. Tapi ketika Anda melihat kembali cerita mereka—kata-kata itu jarang diucapkan saat mereka masih ada untuk menerimanya.',
    story3: 'Orang-orang menulis pesan untuk yang telah tiada seolah-olah mereka bisa membacanya. Mungkin bisa. Mungkin tidak. Saya selalu bertanya-tanya. Tapi yang kita tahu adalah ini: yang hidup dapat mendengar kita sekarang.',
    story4: 'Say It NOW ada untuk mengingatkan kita mengucapkan kata-kata sebelum terlambat. Memberikan orang ruang—dan alasan—untuk mengekspresikan cinta, rasa syukur, kekaguman, dan penghargaan selagi orang tersebut masih hidup, masih hadir, masih bisa merasakan dampak kata-kata itu.',
    highlight: 'Terkadang, yang dibutuhkan seseorang hanyalah tahu bahwa ada yang peduli. Tahu bahwa mereka penting. Tahu bahwa mereka dilihat, dihargai, dan dicintai. Kata-kata itu bisa mengubah hari seseorang—dan terkadang, bisa mengubah hidupnya. Sangat penting untuk kesehatan mental dan emosional.',
    story5: 'Hidup menjadi sibuk. Kita teralihkan. Kita berasumsi akan ada waktu nanti. Dan terlalu sering, nanti itu tidak pernah datang.',
    closing1: 'Say It NOW dibangun agar tidak ada yang meninggalkan dunia ini tanpa tahu betapa dicintai, dihargai, atau luar biasanya mereka sebenarnya.',
    closing2: 'Katakan sekarang.',
    closing3: 'Selagi mereka masih ada.',
    valuesTitle: 'Nilai Inti Kami',
    value1Title: 'Ekspresikan Cinta Sekarang', value1Desc: 'Jangan menunggu pemakaman untuk berbagi betapa berartinya seseorang bagi Anda.',
    value2Title: 'Koneksi Manusia', value2Desc: 'Membangun jembatan antar hati melalui kata-kata bermakna.',
    value3Title: 'Kesejahteraan Mental', value3Desc: 'Mengetahui bahwa Anda dicintai dan dihargai meningkatkan kesehatan mental.',
    value4Title: 'Tak Pernah Terlupakan', value4Desc: 'Memastikan semua orang mengetahui nilai mereka sebelum terlambat.',
    ctaTitle: 'Siap Mengekspresikan Cinta Anda?',
    ctaSubtitle: 'Mulai buat Living Announcements yang indah hari ini. Hanya butuh beberapa menit untuk mencerahkan hari seseorang.',
    ctaButton: 'Mulai Gratis',
    ctaSecondary: 'Lihat Harga',
  },

  hi: {
    badge: 'हमारा उद्देश्य',
    title: 'Say It NOW क्यों मौजूद है',
    subtitle: 'क्योंकि जीवित लोग अब हमें सुन सकते हैं।',
    story1: 'इस ऐप को बनाने का उद्देश्य सरल, फिर भी गहरा मानवीय है: जब तक हम कर सकते हैं, लोगों के बीच बेहतर संबंध बनाना।',
    story2: 'अक्सर, हम वास्तव में यह नहीं सुनते कि कोई कितना प्रिय, प्रशंसित या महत्वपूर्ण है, जब तक कि वे चले नहीं जाते। वर्षों से, मैंने एक ही पैटर्न को दोहराते हुए देखा है—कोई गुजर जाता है, और अचानक उनके सोशल मीडिया पर पोस्ट भर जाते हैं जो बताते हैं कि वे कितने अद्भुत थे, उनका कितना मतलब था, उन्हें कितनी गहराई से याद किया जाएगा। लेकिन जब आप उनकी कहानी, उनकी समय-सीमा, उनके जीवन को देखते हैं—तो वे शब्द शायद ही कभी कहे गए थे जब वे उन्हें प्राप्त करने के लिए यहां थे।',
    story3: 'लोग मृतकों को संदेश लिखते हैं जैसे कि वे उन्हें पढ़ सकते हैं। शायद वे पढ़ सकते हैं। शायद वे नहीं पढ़ सकते। मैंने हमेशा सोचा है। लेकिन हम जो जानते हैं वह यह है: जीवित लोग अब हमें सुन सकते हैं।',
    story4: 'Say It NOW हमें याद दिलाने के लिए मौजूद है कि देर होने से पहले शब्द कहें। यह लोगों को प्यार, कृतज्ञता, प्रशंसा और सराहना व्यक्त करने के लिए एक जगह—और एक कारण—देता है, जबकि व्यक्ति अभी भी जीवित है, अभी भी मौजूद है, अभी भी उन शब्दों के प्रभाव को महसूस करने में सक्षम है।',
    highlight: 'कभी-कभी, एक व्यक्ति को बस यह जानने की आवश्यकता होती है कि कोई परवाह करता है। यह जानने के लिए कि वे मायने रखते हैं। यह जानने के लिए कि उन्हें देखा जाता है, महत्व दिया जाता है और प्यार किया जाता है। वे शब्द किसी के दिन को बदल सकते हैं—और कभी-कभी, वे किसी के जीवन को बदल सकते हैं। वे मानसिक और भावनात्मक स्वास्थ्य के लिए गहराई से मायने रखते हैं, भले ही हम हमेशा इसे महसूस न करें।',
    story5: 'जीवन व्यस्त हो जाता है। हम विचलित हो जाते हैं। हम मानते हैं कि बाद में समय होगा। और अक्सर, बाद में कभी नहीं आता।',
    closing1: 'Say It NOW इसलिए बनाया गया था ताकि कोई भी इस दुनिया को यह जाने बिना न छोड़े कि वे वास्तव में कितने प्रिय, प्रशंसित या अविश्वसनीय हैं।',
    closing2: 'अभी कहो।',
    closing3: 'जब वे अभी भी यहाँ हैं।',
    valuesTitle: 'हमारे मूल मूल्य',
    value1Title: 'अभी प्यार व्यक्त करें', value1Desc: 'किसी के लिए आप कितना मायने रखते हैं, यह साझा करने के लिए अंतिम संस्कार का इंतजार न करें।',
    value2Title: 'मानवीय संबंध', value2Desc: 'सार्थक शब्दों के माध्यम से दिलों के बीच पुल बनाना।',
    value3Title: 'मानसिक स्वास्थ्य', value3Desc: 'यह जानना कि आप प्रिय और प्रशंसित हैं, मानसिक स्वास्थ्य में सुधार करता है।',
    value4Title: 'कभी नहीं भुलाया गया', value4Desc: 'यह सुनिश्चित करना कि देर होने से पहले हर कोई अपना मूल्य जानता है।',
    ctaTitle: 'अपना प्यार व्यक्त करने के लिए तैयार हैं?',
    ctaSubtitle: 'आज ही सुंदर Living Announcements बनाना शुरू करें। किसी के दिन को रोशन करने में केवल कुछ मिनट लगते हैं।',
    ctaButton: 'मुफ्त में शुरू करें',
    ctaSecondary: 'मूल्य देखें',
  },
};

export default function About() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const c = lc(copy, language);
  useDocumentHead({
    title: 'About Say It NOW — Why We Help You Say It While They\'re Here',
    description: 'Why Say It NOW exists: helping people express love, gratitude, and appreciation while their words can still be heard.',
    path: '/about',
  });

  const handlePricingClick = () => {
    navigate('/');
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing');
      pricingSection?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };
  return (
    <div className="min-h-screen bg-hero-gradient">
      <Header />
      
      <main className="container py-16 md:py-24">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Heart className="h-4 w-4" fill="currentColor" />
            <span className="text-sm font-medium">{c.badge}</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            {c.title}
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {c.subtitle}
          </p>
        </motion.div>

        {/* Main Story */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-4xl mx-auto mb-20"
        >
          <div className="glass border-0 rounded-3xl p-8 md:p-12 shadow-glass-lg">
            <div className="prose prose-lg max-w-none text-foreground">
              <p className="text-lg md:text-xl leading-relaxed mb-6">{c.story1}</p>
              <p className="text-lg md:text-xl leading-relaxed mb-6">{c.story2}</p>
              <p className="text-lg md:text-xl leading-relaxed mb-6 italic text-muted-foreground">{c.story3}</p>
              <p className="text-lg md:text-xl leading-relaxed mb-6">{c.story4}</p>

              <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg my-8">
                <p className="text-lg md:text-xl leading-relaxed font-medium text-foreground">{c.highlight}</p>
              </div>

              <p className="text-lg md:text-xl leading-relaxed mb-6">{c.story5}</p>
              <p className="text-xl md:text-2xl leading-relaxed font-semibold text-primary mb-6">{c.closing1}</p>
              <p className="text-xl md:text-2xl leading-relaxed font-bold text-foreground text-center">
                {c.closing2}<br />
                <span className="text-primary">{c.closing3}</span>
              </p>
            </div>
          </div>
        </motion.div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-20"
        >
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
            {c.valuesTitle}
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: c.value1Title, description: c.value1Desc },
              { icon: Users, title: c.value2Title, description: c.value2Desc },
              { icon: MessageCircleHeart, title: c.value3Title, description: c.value3Desc },
              { icon: HandHeart, title: c.value4Title, description: c.value4Desc },
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                className="glass border-0 rounded-2xl p-6 text-center hover:shadow-soft-lg transition-all duration-300"
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-primary/10 rounded-full mb-4">
                  <value.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{value.title}</h3>
                <p className="text-muted-foreground text-sm">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center bg-gradient-to-br from-primary/10 via-background to-primary/5 rounded-3xl p-12"
        >
          <Sparkles className="h-12 w-12 text-primary mx-auto mb-6" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">{c.ctaTitle}</h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">{c.ctaSubtitle}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">{c.ctaButton}</Button>
            </Link>
            <Button size="lg" variant="outline" onClick={handlePricingClick}>{c.ctaSecondary}</Button>
          </div>
        </motion.div>
      </main>

      <Footer />
    </div>
  );
}
